import { backendClient } from '../lib/api/backendClient';
import { assertSpotifyUserId } from '../lib/auth/spotifyUserId';

const toIsoString = (value?: string | number | Date | null): string | undefined => {
  if (value == null) return undefined;
  if (value instanceof Date) return value.toISOString();
  if (typeof value === 'number' && Number.isFinite(value)) {
    const parsed = new Date(value);
    return Number.isNaN(parsed.valueOf()) ? undefined : parsed.toISOString();
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed.length) return undefined;
    const parsed = new Date(trimmed);
    return Number.isNaN(parsed.valueOf()) ? undefined : parsed.toISOString();
  }
  return undefined;
};

export type FavoriteAlbumMetadata = Record<string, unknown>;

export const favoriteAlbumsService = {
  async saveFavoriteAlbum(
    spotifyAlbumId: string,
    options?: {
      addedAt?: string | number | Date | null;
      savedAt?: string | number | Date | null;
      metadata?: FavoriteAlbumMetadata | null;
    }
  ): Promise<void> {
    const userId = assertSpotifyUserId();
    const payload: Record<string, unknown> = {
      userId,
      spotifyAlbumId,
    };

    const addedAt = toIsoString(options?.addedAt ?? undefined);
    if (addedAt) payload.addedAt = addedAt;

    const savedAt = toIsoString(options?.savedAt ?? undefined);
    if (savedAt) payload.savedAt = savedAt;

    if (options?.metadata && typeof options.metadata === 'object') {
      payload.metadata = options.metadata;
    }

    await backendClient.post('/favorites/albums', payload);
  },

  async updateFavoriteAlbum(
    spotifyAlbumId: string,
    options?: {
      addedAt?: string | number | Date | null;
      savedAt?: string | number | Date | null;
      metadata?: FavoriteAlbumMetadata | null;
    }
  ): Promise<void> {
    const userId = assertSpotifyUserId();
    const payload: Record<string, unknown> = { userId };

    const addedAt = toIsoString(options?.addedAt ?? undefined);
    if (addedAt) payload.addedAt = addedAt;

    const savedAt = toIsoString(options?.savedAt ?? undefined);
    if (savedAt) payload.savedAt = savedAt;

    if (options?.metadata && typeof options.metadata === 'object') {
      payload.metadata = options.metadata;
    }

    await backendClient.put(`/favorites/albums/${encodeURIComponent(spotifyAlbumId)}`, payload);
  },

  async removeFavoriteAlbum(spotifyAlbumId: string): Promise<void> {
    const userId = assertSpotifyUserId();
    await backendClient.delete(`/favorites/albums/${encodeURIComponent(spotifyAlbumId)}`, {
      params: { userId },
    });
  },
};

